import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
	CommonState,
	LOGIN_LIMIT_PROGRESS_ACTION_TYPE,
	LoginLimitAction,
	LoginLimitSelector,
	LoginLimitService,
} from "@obg/ngx.common";
import {
	AppError,
	AuthAction,
	Jurisdiction,
	JurisdictionSelector,
	LimitsReminderAction,
	MessageType,
	SESSION_ACTION_TYPE,
	SessionEndReason,
} from "@obg/ngx.core";
import { CoreContentState } from "@obg/ngx.core.content";
import { LoginLimitReminderService } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { millisecondsToMinutes } from "date-fns";
import { of } from "rxjs";
import { catchError, filter, map, switchMap, take, tap, withLatestFrom } from "rxjs/operators";

import { CustomerInteractionsAction } from "../customer-interactions/customer-interactions.action";
import { MatNotificationsService } from "../mat-notifications/mat-notifications.service";
import { RealTimeNotificationsAction } from "../real-time-notifications/real-time-notifications.action";
import { RealTimeNotificationsConfig } from "../real-time-notifications/real-time-notifications.config";
import {
	CustomerInteractionRealTimeMessagePayload,
	RealtimeMessageWithMessageId,
} from "../real-time-notifications/real-time-notifications.model";

import { CustomerLimitNotificationsAction } from "./customer-limit-notifications.actions";
import { SessionLimitNotificationPayload } from "./customer-limit-notifications.model";
import { SessionLimitNotificationService } from "./session-limit-notification.service";

@Injectable()
export class CustomerLimitNotificationsEffects {
	sessionLimitNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getSessionLimitNotificationsEnabled()),
			map(({ customerId }) =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getSessionLimitNotificationTopic().concat(customerId)
				)
			)
		)
	);

	responsibleGamingNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getResponsibleGamingNotificationEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getResponsibleGamingNotificationTopic()
				)
			)
		)
	);

	selfExclusionNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getSelfExclusionNotificationEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getSelfExclusionNotificationTopic()
				)
			)
		)
	);

	betlimitNotifications$ = createEffect(() =>
		this.actions$.pipe(
			ofType(RealTimeNotificationsAction.connectionSuccess),
			filter(() => this.realTimeNotificationsConfig.getBetLimitNotificationEnabled()),
			map(() =>
				RealTimeNotificationsAction.subscribeToChannel(
					this.realTimeNotificationsConfig.getBetLimitNotificationTopic()
				)
			)
		)
	);

	openSnackBar$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSnackBar),
				switchMap(({ snackbarOpts }) => this.notificationsService.openCustomerLimitSnackbar(snackbarOpts))
			),
		{ dispatch: false }
	);

	openAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openAlert),
				switchMap(({ alert }) =>
					this.notificationsService.openCustomerLimitAlert(
						alert.limitPrefix,
						alert.redirectButtonAction,
						alert.prefixJurisdiction,
						alert.buttonStyle
					)
				)
			),
		{ dispatch: false }
	);

	openSessionExpiryAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSessionExpiryAlert),
				switchMap(({ alert }) =>
					this.actions$.pipe(
						ofType(SESSION_ACTION_TYPE.logoutSuccess),
						switchMap(() =>
							this.notificationsService.openCustomerLimitAlert(
								alert.limitPrefix,
								alert.redirectButtonAction,
								alert.prefixJurisdiction
							)
						)
					)
				)
			),
		{ dispatch: false }
	);

	openSessionLimitNotification$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSessionLimitSnackbar),
				switchMap(({ snackbarOpts }) => this.notificationsService.openSessionLimitSnackbar(snackbarOpts))
			),
		{ dispatch: false }
	);

	openSelfExclusionAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSelfExclusionAlert),
				switchMap(() => this.notificationsService.openSelfExclusionAlert$())
			),
		{ dispatch: false }
	);

	openBalanceLimitExceededAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openBalanceLimitExceededAlert),
				tap((payload) => {
					const customerInteractionMessage =
						payload as RealtimeMessageWithMessageId as CustomerInteractionRealTimeMessagePayload;
					return this.store.dispatch(CustomerInteractionsAction.markAlertAsShown(customerInteractionMessage));
				}),
				switchMap((payload) => this.notificationsService.openBalanceLimitExceededAlert$(payload))
			),
		{ dispatch: false }
	);

	openDepositLimitReachedAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openDepositLimitReachedAlert),
				tap((payload) => {
					const customerInteractionMessage =
						payload as RealtimeMessageWithMessageId as CustomerInteractionRealTimeMessagePayload;
					return this.store.dispatch(CustomerInteractionsAction.markAlertAsShown(customerInteractionMessage));
				}),
				switchMap((payload) => this.notificationsService.openDepositLimitReachedAlert$(payload))
			),
		{ dispatch: false }
	);

	openBettingLimitReachedAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openBettingLimitReachedAlert),
				switchMap((payload) => this.notificationsService.openBettingLimitReachedAlert$(payload))
			),
		{ dispatch: false }
	);

	openSessionLimitReachedAlert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSessionLimitReachedAlert),
				switchMap(() => this.notificationsService.openSessionLimitReachedAlert$()),
				tap(() => this.store.dispatch(this.authAction.sessionEnd(SessionEndReason.SessionExpired)))
			),
		{ dispatch: false }
	);

	handleSessionLimitNotification$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerLimitNotificationsAction.handleSessionLimitNotification),
			map((sessionLimitPayload) =>
				this.realTimeNotificationsConfig.getSessionLimitV2Enabled()
					? LimitsReminderAction.fetchNotificationLimitsSummary()
					: CustomerLimitNotificationsAction.openSessionLimitSnackbar({
							message: `user-summary.session-limit-warning.${millisecondsToMinutes(
								sessionLimitPayload.remainingSessionTimeInMillis!
							)}.message`,
							type: MessageType.Info,
							doNotShowAgainButton: false,
							prefixJurisdiction: true,
							dismissibleDuration: 0,
							duration: 5000,
							dismissible: true,
						})
			)
		)
	);

	openSessionLimitNotificationDialog$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerLimitNotificationsAction.openSessionLimitNotificationDialog),
				switchMap((payload: SessionLimitNotificationPayload) =>
					this.sessionLimitNotificationService.openSessionLimitReminderAlert$(payload)
				)
			),
		{ dispatch: false }
	);

	handleLoginLimitNotificationDialog$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SESSION_ACTION_TYPE.loginSuccess),
				withLatestFrom(this.store.select(JurisdictionSelector.get)),
				filter(([, jurisdiction]) => jurisdiction === Jurisdiction.Zhh),
				switchMap(() =>
					this.loginLimitService.getProgress().pipe(
						map((response) => this.loginLimitAction.getProgressSuccess(response)),
						catchError((response: AppError) => of(this.loginLimitAction.getFail(response))),
						take(1)
					)
				)
			),
		{ dispatch: true }
	);

	openLoginLimitNotificationDialog$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(LOGIN_LIMIT_PROGRESS_ACTION_TYPE.getSuccess),
				concatLatestFrom(() => this.commonStore.select(this.loginLimitSelector.getLoginLimitProgress())),
				filter(
					([, loginLimitsProgress]) =>
						!!loginLimitsProgress?.currentProgress?.loginCountLimitStatuses[0]?.shouldNotify
				),
				tap(() => this.service.openZhhLoginLimitReminderAlert())
			),
		{ dispatch: false }
	);

	postSessionStartLimitCheck$ = createEffect(() =>
		this.actions$.pipe(
			ofType(SESSION_ACTION_TYPE.loginSuccess),
			map(() => LimitsReminderAction.fetchNotificationLimitsSummary())
		)
	);

	checkRemainingSessionLimit$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(LimitsReminderAction.fetchNotificationLimitsSummarySuccess),
				ignoreNil(),
				tap((s) =>
					this.sessionLimitNotificationService.checkRemainingSessionLimit(
						s.limitsSummary.sessionLimitInfoResponse
					)
				)
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private realTimeNotificationsConfig: RealTimeNotificationsConfig,
		private notificationsService: MatNotificationsService,
		private sessionLimitNotificationService: SessionLimitNotificationService,
		private store: Store<CoreContentState>,
		private authAction: AuthAction,
		private loginLimitAction: LoginLimitAction,
		private loginLimitService: LoginLimitService,
		private commonStore: Store<CommonState>,
		private loginLimitSelector: LoginLimitSelector,
		private service: LoginLimitReminderService
	) {}
}
